import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import FieldEditor from './FieldEditor';
import CustomLayoutThumb from './CustomLayoutThumb';
import { DesignerSection, DESIGNER_SECTION_TYPES } from './DesignerSections';
import {
  Plus, Save, Loader2, GripVertical, Eye, EyeOff, Edit2, Copy, Trash2, X,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('admin_token');
  return t ? { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const TEMPLATE_ICONS = {
  media_gallery: '🖼',
  document_viewer: '📄',
  feature_grid_diagonal: '◇',
  bento_layout: '▦',
  step_timeline: '⏱',
  layered_panels: '▧',
  layered_panels_6: '▧',
  layered_panels_8: '▧',
  custom_layout: '✦',
  speaker_preview: '🎤',
  sponsors: '★',
  program_highlights: '📋',
  featured_nation: '🌍',
};

const PageSectionsManager = ({ page, title = 'Additional Sections' }) => {
  const [sections, setSections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [editContent, setEditContent] = useState({});
  const [saving, setSaving] = useState(false);
  const [draggedId, setDraggedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [secRes, tplRes] = await Promise.all([
        fetch(`${API_URL}/api/homepage/sections?page=${encodeURIComponent(page)}`),
        fetch(`${API_URL}/api/homepage/templates`),
      ]);
      if (secRes.ok) setSections(await secRes.json());
      if (tplRes.ok) setTemplates(await tplRes.json());
    } catch (e) {
      console.error('Failed to fetch sections:', e);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getTemplate = (section) => {
    const key = section.template_id || `tpl_${section.section_type}`;
    return templates.find(t => t.id === key || t.section_type === section.section_type);
  };

  const addSection = async (sectionType, templateId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/homepage/sections?section_type=${sectionType}&template_id=${templateId}&page=${encodeURIComponent(page)}`,
        { method: 'POST', headers: authHeaders() }
      );
      if (res.ok) {
        const created = await res.json();
        toast.success('Section added');
        await fetchData();
        startEditing(created);
      }
    } catch { toast.error('Failed to add section'); }
    setShowPicker(false);
  };

  const deleteSection = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    try {
      await fetch(`${API_URL}/api/homepage/sections/${id}`, { method: 'DELETE', headers: authHeaders() });
      toast.success('Section deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const toggleVisibility = async (id, visible) => {
    try {
      await fetch(`${API_URL}/api/homepage/sections/${id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ visible }),
      });
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const duplicateSection = async (id) => {
    try {
      await fetch(`${API_URL}/api/homepage/sections/${id}/duplicate`, { method: 'POST', headers: authHeaders() });
      toast.success('Section duplicated');
      fetchData();
    } catch { toast.error('Failed to duplicate'); }
  };

  const startEditing = (section) => {
    setEditingSection(section);
    setEditContent(section.draft_content || section.content || {});
  };

  const saveSection = async () => {
    if (!editingSection) return;
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/homepage/sections/${editingSection.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ content: editContent }),
      });
      toast.success('Section saved');
      setEditingSection(null);
      setEditContent({});
      fetchData();
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) return;
    const dragIdx = sections.findIndex(s => s.id === draggedId);
    const targetIdx = sections.findIndex(s => s.id === targetId);
    const next = [...sections];
    const [removed] = next.splice(dragIdx, 1);
    next.splice(targetIdx, 0, removed);
    const orders = next.map((s, i) => ({ id: s.id, order: i }));
    try {
      await fetch(`${API_URL}/api/homepage/sections/reorder`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(orders),
      });
      fetchData();
    } catch { toast.error('Reorder failed'); }
    setDraggedId(null);
  };

  // Filter templates: exclude page-specific ones like hero/about/stats/action_cards/registration_cta/location
  const pageSpecificTypes = new Set(['hero', 'about', 'stats', 'action_cards', 'registration_cta', 'location']);
  const availableTemplates = templates.filter(t => !pageSpecificTypes.has(t.section_type));

  if (loading) return <div className="py-6 text-center text-sm text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading sections...</div>;

  return (
    <div className="mt-8 border-t pt-6" data-testid={`page-sections-manager-${page}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-800">{title}</h2>
        <Button variant="outline" size="sm" onClick={() => setShowPicker(true)} data-testid={`add-section-${page}`}>
          <Plus className="w-4 h-4 mr-1" /> Add Section
        </Button>
      </div>

      {sections.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-8">No additional sections yet. Click &ldquo;Add Section&rdquo; to get started.</p>
      )}

      {/* Section list */}
      {sections.map((section) => (
        <div
          key={section.id}
          draggable
          onDragStart={(e) => { setDraggedId(section.id); e.dataTransfer.effectAllowed = 'move'; }}
          onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
          onDrop={(e) => handleDrop(e, section.id)}
          className={`bg-white border rounded-lg p-4 mb-3 transition-all hover:shadow-md ${!section.visible ? 'opacity-60' : ''}`}
          data-testid={`page-section-card-${section.id}`}
        >
          <div className="flex items-center gap-4">
            <div className="cursor-grab text-gray-400 hover:text-gray-600"><GripVertical className="w-5 h-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-medium text-gray-900 truncate">{section.name}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full ${section.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                  {section.status}
                </span>
              </div>
              <p className="text-sm text-gray-500">Type: {section.section_type}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <button onClick={() => toggleVisibility(section.id, !section.visible)} className={`p-2 rounded-md ${section.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} title={section.visible ? 'Hide' : 'Show'}>
                {section.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => startEditing(section)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-md" title="Edit">
                <Edit2 className="w-4 h-4" />
              </button>
              <button onClick={() => duplicateSection(section.id)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-md" title="Duplicate">
                <Copy className="w-4 h-4" />
              </button>
              <button onClick={() => deleteSection(section.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-md" title="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Template picker modal */}
      {showPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPicker(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()} data-testid={`template-picker-${page}`}>
            <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-bold text-lg">Add Section</h3>
              <button onClick={() => setShowPicker(false)} className="p-1 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-5">
              {availableTemplates.map((tpl) => {
                const isCustom = tpl.is_custom && tpl.layout_preview;
                return (
                  <button
                    key={tpl.id}
                    onClick={() => addSection(tpl.section_type, tpl.id)}
                    className="border rounded-xl p-3 text-left hover:border-[#00457f] hover:shadow-md transition-all group"
                    data-testid={`tpl-pick-${tpl.section_type}`}
                  >
                    <div className="h-16 bg-slate-50 rounded-lg mb-2 flex items-center justify-center text-2xl text-slate-400 overflow-hidden">
                      {isCustom ? (
                        <CustomLayoutThumb layout={tpl.layout_preview} />
                      ) : (
                        <span>{TEMPLATE_ICONS[tpl.section_type] || '▧'}</span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-[#00457f]">{tpl.name}</p>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{tpl.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Section editor slide-out */}
      {editingSection && (
        <div className="fixed inset-0 bg-black/50 flex justify-end z-50">
          <div className="w-full max-w-lg bg-white h-full flex flex-col overflow-hidden" data-testid={`section-editor-${page}`}>
            <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10">
              <div>
                <h2 className="font-bold text-lg">{editingSection.name}</h2>
                <p className="text-sm text-gray-500">{editingSection.section_type}</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setEditingSection(null); setEditContent({}); }}>Cancel</Button>
                <Button onClick={saveSection} disabled={saving} data-testid={`save-section-${page}`}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Save
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {(() => {
                const tpl = getTemplate(editingSection);
                return tpl?.fields?.map((field) => (
                  <FieldEditor
                    key={field.name}
                    field={field}
                    value={editContent[field.name]}
                    onChange={(name, val) => setEditContent(prev => ({ ...prev, [name]: val }))}
                  />
                ));
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageSectionsManager;
